import type { SupabaseClient } from '@supabase/supabase-js';
import { walletAppConfig } from './config';
import { translate } from '../i18n';

const OAUTH_TRANSACTION_KEY = 'bubble-wallet-central-sso-transaction';
const CALLBACK_PATH = '/auth/callback';
const REQUEST_TIMEOUT_MS = 15_000;
const TRANSACTION_MAX_AGE_MS = 10 * 60 * 1000;
const POPUP_CALLBACK_MESSAGE_TYPE = 'bubble-wallet-central-sso-popup-callback-v1';
const POPUP_CALLBACK_ACK_MESSAGE_TYPE = 'bubble-wallet-central-sso-popup-callback-ack-v1';
const POPUP_RELAY_MARKER_KEY = 'bubble-wallet-central-sso-popup-relay-marker';
const POPUP_CALLBACK_RETRY_MS = 300;
const POPUP_CALLBACK_ACK_TIMEOUT_MS = 15_000;
const SILENT_PREPARE_PARAMETER = 'central_sso_silent';
const SILENT_PREPARE_VALUE = 'prepare';
const SILENT_RELAY_MARKER_KEY = 'bubble-wallet-central-sso-silent-relay-marker';
const SILENT_READY_MESSAGE_TYPE = 'bubble-wallet-central-sso-silent-ready-v1';
const SILENT_FRAME_ERROR_MESSAGE_TYPE = 'bubble-wallet-central-sso-silent-frame-error-v1';
const SILENT_CALLBACK_MESSAGE_TYPE = 'bubble-wallet-central-sso-silent-callback-v1';
let callbackCompletionPromise: Promise<boolean> | null = null;

type OAuthTransaction = {
  state: string;
  codeVerifier: string;
  nonce: string;
  redirectUri: string;
  createdAt: number;
};

type TokenResponse = {
  access_token?: unknown;
  refresh_token?: unknown;
};

type CentralSsoCallbackResponse = {
  code?: string;
  state?: string;
  error?: string;
};

export type CentralSsoPopupCallbackMessage = {
  type: typeof POPUP_CALLBACK_MESSAGE_TYPE;
  callback: CentralSsoCallbackResponse;
};

export type CentralSsoPopupCallbackAcknowledgement = {
  type: typeof POPUP_CALLBACK_ACK_MESSAGE_TYPE;
  state: string;
};

export type CentralSsoPopupAttempt = {
  popup: Window;
  ready: Promise<void>;
};

export type CentralSsoSilentAttempt = {
  iframe: HTMLIFrameElement;
  authorizeUrl: string;
};

export type CentralSsoSilentCallbackMessage = {
  type: typeof SILENT_CALLBACK_MESSAGE_TYPE;
  callback: CentralSsoCallbackResponse;
};

export type CentralSsoErrorCode =
  | 'central_sso_not_configured'
  | 'central_sso_issuer_mismatch'
  | 'central_sso_storage_failed'
  | 'central_sso_state_mismatch'
  | 'central_sso_missing_callback'
  | 'central_sso_token_exchange_failed'
  | 'central_sso_invalid_token_response'
  | 'central_sso_session_failed'
  | 'central_sso_popup_blocked'
  | 'central_sso_popup_failed'
  | 'central_sso_popup_closed'
  | 'access_denied'
  | 'invalid_request'
  | 'invalid_client'
  | 'invalid_redirect_uri'
  | 'invalid_grant'
  | 'pkce_verification_failed';

export class CentralSsoError extends Error {
  code: CentralSsoErrorCode;

  constructor(code: CentralSsoErrorCode) {
    super(code);
    this.name = 'CentralSsoError';
    this.code = code;
  }
}

export type CentralSsoDiagnosticFlow = 'bootstrap' | 'silent' | 'top_level' | 'popup' | 'callback';

export type CentralSsoDiagnosticValue = string | number | boolean | null | undefined;

export function logCentralSsoDiagnostic(
  stage: string,
  details: Record<string, CentralSsoDiagnosticValue> = {},
): void {
  console.info('[Bubble Wallet][Central SSO]', stage, details);
}

export function getCentralSsoDiagnosticErrorCode(error: unknown): string {
  if (error instanceof CentralSsoError) {
    return error.code;
  }
  if (error instanceof Error) {
    return error.name;
  }
  return 'unknown';
}

function getCallbackDiagnosticFields(response: CentralSsoCallbackResponse): Record<string, boolean> {
  return {
    hasCode: typeof response.code === 'string',
    hasState: typeof response.state === 'string',
    hasError: typeof response.error === 'string',
  };
}

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return encodeBase64Url(bytes);
}

function getIssuerUrl(): string {
  return walletAppConfig.centralAuthIssuerUrl.replace(/\/$/, '');
}

function getRedirectUri(): string {
  return walletAppConfig.centralAuthRedirectUri.trim();
}

function getClientId(): string {
  return walletAppConfig.centralAuthClientId.trim();
}

function getScope(): string {
  return import.meta.env.VITE_CENTRAL_AUTH_SCOPE?.trim() || 'openid profile';
}

function getConfig(): { issuerUrl: string; clientId: string; redirectUri: string; scope: string } {
  const issuerUrl = getIssuerUrl();
  const clientId = getClientId();
  const redirectUri = getRedirectUri();

  if (!walletAppConfig.supabaseUrl || !walletAppConfig.publishableKey || !issuerUrl || !clientId || !redirectUri) {
    throw new CentralSsoError('central_sso_not_configured');
  }

  if (issuerUrl !== walletAppConfig.supabaseUrl.replace(/\/$/, '')) {
    throw new CentralSsoError('central_sso_issuer_mismatch');
  }

  return { issuerUrl, clientId, redirectUri, scope: getScope() };
}

function readTransaction(flow: CentralSsoDiagnosticFlow): OAuthTransaction | null {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_TRANSACTION_KEY);
    if (!raw) {
      logCentralSsoDiagnostic('transaction_missing', { flow });
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<OAuthTransaction>;
    if (
      typeof parsed.state !== 'string' ||
      typeof parsed.codeVerifier !== 'string' ||
      typeof parsed.nonce !== 'string' ||
      typeof parsed.redirectUri !== 'string' ||
      typeof parsed.createdAt !== 'number'
    ) {
      logCentralSsoDiagnostic('transaction_invalid', { flow });
      return null;
    }
    return parsed as OAuthTransaction;
  } catch {
    logCentralSsoDiagnostic('transaction_read_failed', { flow });
    return null;
  }
}

function clearTransaction(): void {
  try {
    window.sessionStorage.removeItem(OAUTH_TRANSACTION_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function saveTransaction(transaction: OAuthTransaction, flow: CentralSsoDiagnosticFlow): void {
  try {
    window.sessionStorage.setItem(OAUTH_TRANSACTION_KEY, JSON.stringify(transaction));
    logCentralSsoDiagnostic('transaction_saved', {
      flow,
      hasState: Boolean(transaction.state),
      hasCodeVerifier: Boolean(transaction.codeVerifier),
      hasNonce: Boolean(transaction.nonce),
    });
  } catch {
    logCentralSsoDiagnostic('transaction_save_failed', { flow });
    throw new CentralSsoError('central_sso_storage_failed');
  }
}

async function createCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier),
  );
  return encodeBase64Url(new Uint8Array(digest));
}

function scrubCallbackUrl(): void {
  window.history.replaceState({}, '', window.location.pathname);
}

function getCallbackResponse(params: URLSearchParams): CentralSsoCallbackResponse {
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  return {
    ...(code ? { code } : {}),
    ...(state ? { state } : {}),
    ...(error ? { error } : {}),
  };
}

export function isCentralSsoCallbackPath(): boolean {
  return window.location.pathname === CALLBACK_PATH;
}

export function hasCentralSsoCallbackResponse(): boolean {
  if (!isCentralSsoCallbackPath()) {
    return false;
  }

  const response = getCallbackResponse(new URLSearchParams(window.location.search));
  return Boolean(response.error || response.code || response.state);
}

export function isCentralSsoPopupCallbackMessage(
  value: unknown,
): value is CentralSsoPopupCallbackMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const message = value as Record<string, unknown>;
  if (message.type !== POPUP_CALLBACK_MESSAGE_TYPE || !message.callback || typeof message.callback !== 'object') {
    return false;
  }

  const callback = message.callback as Record<string, unknown>;
  return Object.keys(message).every((key) => key === 'type' || key === 'callback')
    && Object.keys(callback).every((key) => key === 'code' || key === 'state' || key === 'error')
    && (callback.code === undefined || typeof callback.code === 'string')
    && (callback.state === undefined || typeof callback.state === 'string')
    && (callback.error === undefined || typeof callback.error === 'string')
    && (typeof callback.code === 'string' || typeof callback.state === 'string' || typeof callback.error === 'string');
}

function isExactTypeMessage(value: unknown, type: string): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const message = value as Record<string, unknown>;
  return message.type === type && Object.keys(message).length === 1;
}

export function isCentralSsoSilentReadyMessage(value: unknown): boolean {
  return isExactTypeMessage(value, SILENT_READY_MESSAGE_TYPE);
}

export function isCentralSsoSilentFrameErrorMessage(value: unknown): boolean {
  return isExactTypeMessage(value, SILENT_FRAME_ERROR_MESSAGE_TYPE);
}

export function isCentralSsoSilentCallbackMessage(
  value: unknown,
): value is CentralSsoSilentCallbackMessage {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const message = value as Record<string, unknown>;
  if (message.type !== SILENT_CALLBACK_MESSAGE_TYPE || !message.callback || typeof message.callback !== 'object') {
    return false;
  }

  const callback = message.callback as Record<string, unknown>;
  return Object.keys(message).every((key) => key === 'type' || key === 'callback')
    && Object.keys(callback).every((key) => key === 'code' || key === 'state' || key === 'error')
    && (callback.code === undefined || typeof callback.code === 'string')
    && (callback.state === undefined || typeof callback.state === 'string')
    && (callback.error === undefined || typeof callback.error === 'string')
    && (typeof callback.code === 'string' || typeof callback.state === 'string' || typeof callback.error === 'string');
}

function isIframeWithParent(): boolean {
  return Boolean(window.parent && window.parent !== window);
}

function postToWalletParent(message: object): void {
  window.parent?.postMessage(message, window.location.origin);
}

export function prepareCentralSsoSilentFrame(): boolean {
  if (
    !isCentralSsoCallbackPath()
    || !isIframeWithParent()
    || new URLSearchParams(window.location.search).get(SILENT_PREPARE_PARAMETER) !== SILENT_PREPARE_VALUE
  ) {
    return false;
  }

  logCentralSsoDiagnostic('silent_frame_prepare_started', {
    flow: 'silent',
    isIframe: true,
    hasParent: true,
  });
  try {
    window.sessionStorage.setItem(SILENT_RELAY_MARKER_KEY, randomBase64Url(32));
    scrubCallbackUrl();
    postToWalletParent({ type: SILENT_READY_MESSAGE_TYPE });
    logCentralSsoDiagnostic('silent_frame_ready_posted', {
      flow: 'silent',
      markerStored: true,
      callbackUrlScrubbed: true,
    });
  } catch {
    logCentralSsoDiagnostic('silent_frame_prepare_failed', {
      flow: 'silent',
      markerStored: false,
    });
    postToWalletParent({ type: SILENT_FRAME_ERROR_MESSAGE_TYPE });
  }
  return true;
}

export function relayCentralSsoSilentCallback(): boolean {
  if (!isCentralSsoCallbackPath() || !isIframeWithParent()) {
    return false;
  }

  let relayMarker: string | null = null;
  try {
    relayMarker = window.sessionStorage.getItem(SILENT_RELAY_MARKER_KEY);
    window.sessionStorage.removeItem(SILENT_RELAY_MARKER_KEY);
  } catch {
    logCentralSsoDiagnostic('silent_callback_marker_read_failed', { flow: 'silent' });
    postToWalletParent({ type: SILENT_FRAME_ERROR_MESSAGE_TYPE });
    return true;
  }
  logCentralSsoDiagnostic('silent_callback_marker_checked', {
    flow: 'silent',
    markerPresent: Boolean(relayMarker),
  });
  if (!relayMarker) {
    return false;
  }

  const response = getCallbackResponse(new URLSearchParams(window.location.search));
  if (!response.error && !response.code && !response.state) {
    logCentralSsoDiagnostic('silent_callback_missing_response', { flow: 'silent' });
    postToWalletParent({ type: SILENT_FRAME_ERROR_MESSAGE_TYPE });
    return true;
  }

  scrubCallbackUrl();
  postToWalletParent({
    type: SILENT_CALLBACK_MESSAGE_TYPE,
    callback: response,
  } satisfies CentralSsoSilentCallbackMessage);
  logCentralSsoDiagnostic('silent_callback_relayed', {
    flow: 'silent',
    ...getCallbackDiagnosticFields(response),
    callbackUrlScrubbed: true,
  });
  return true;
}

export function relayCentralSsoPopupCallback(): boolean {
  if (
    !hasCentralSsoCallbackResponse()
    || !window.opener
    || window.opener === window
  ) {
    return false;
  }

  let popupRelayMarker: string | null = null;
  try {
    popupRelayMarker = window.sessionStorage.getItem(POPUP_RELAY_MARKER_KEY);
    window.sessionStorage.removeItem(POPUP_RELAY_MARKER_KEY);
  } catch {
    return false;
  }
  if (!popupRelayMarker) {
    return false;
  }

  const response = getCallbackResponse(new URLSearchParams(window.location.search));
  scrubCallbackUrl();
  const message: CentralSsoPopupCallbackMessage = {
    type: POPUP_CALLBACK_MESSAGE_TYPE,
    callback: response,
  };
  let retryId: number | null = null;
  let timeoutId: number | null = null;
  const clearRelay = () => {
    if (retryId !== null) {
      window.clearInterval(retryId);
      retryId = null;
    }
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    window.removeEventListener('message', closeAfterAcknowledgement);
  };
  const sendCallback = () => {
    try {
      window.opener?.postMessage(message, window.location.origin);
    } catch {
      // The bounded timeout below presents a retry message if the opener is gone.
    }
  };
  const closeAfterAcknowledgement = (event: MessageEvent<unknown>) => {
    const acknowledgement = event.data as Partial<CentralSsoPopupCallbackAcknowledgement> | null;
    if (
      event.origin !== window.location.origin
      || event.source !== window.opener
      || acknowledgement?.type !== POPUP_CALLBACK_ACK_MESSAGE_TYPE
      || acknowledgement.state !== (response.state ?? '')
    ) {
      return;
    }
    clearRelay();
    window.close();
  };
  window.addEventListener('message', closeAfterAcknowledgement);
  sendCallback();
  retryId = window.setInterval(sendCallback, POPUP_CALLBACK_RETRY_MS);
  timeoutId = window.setTimeout(() => {
    clearRelay();
    const messageRoot = document.getElementById('root') ?? document.body;
    messageRoot.textContent = translate('errors.ssoPopupResponseMissing');
  }, POPUP_CALLBACK_ACK_TIMEOUT_MS);
  return true;
}

function mapOAuthError(error: string | null): CentralSsoErrorCode {
  switch (error) {
    case 'access_denied':
      return 'access_denied';
    case 'invalid_client':
      return 'invalid_client';
    case 'invalid_redirect_uri':
      return 'invalid_redirect_uri';
    case 'invalid_grant':
      return 'invalid_grant';
    case 'pkce_verification_failed':
      return 'pkce_verification_failed';
    default:
      return 'invalid_request';
  }
}

async function createAuthorizeUrl(flow: CentralSsoDiagnosticFlow): Promise<string> {
  logCentralSsoDiagnostic('authorize_url_creation_started', { flow });
  let config: ReturnType<typeof getConfig>;
  try {
    config = getConfig();
  } catch (error) {
    logCentralSsoDiagnostic('authorize_url_creation_failed', {
      flow,
      errorCode: getCentralSsoDiagnosticErrorCode(error),
    });
    throw error;
  }
  const { issuerUrl, clientId, redirectUri, scope } = config;
  const state = randomBase64Url(32);
  const codeVerifier = randomBase64Url(48);
  const nonce = randomBase64Url(32);
  try {
    const codeChallenge = await createCodeChallenge(codeVerifier);
    saveTransaction({
      state,
      codeVerifier,
      nonce,
      redirectUri,
      createdAt: Date.now(),
    }, flow);

    const authorizeUrl = new URL(`${issuerUrl}/auth/v1/oauth/authorize`);
    authorizeUrl.searchParams.set('response_type', 'code');
    authorizeUrl.searchParams.set('client_id', clientId);
    authorizeUrl.searchParams.set('redirect_uri', redirectUri);
    authorizeUrl.searchParams.set('code_challenge', codeChallenge);
    authorizeUrl.searchParams.set('code_challenge_method', 'S256');
    authorizeUrl.searchParams.set('state', state);
    authorizeUrl.searchParams.set('scope', scope);
    if (scope.split(/\s+/).includes('openid')) {
      authorizeUrl.searchParams.set('nonce', nonce);
    }
    logCentralSsoDiagnostic('authorize_url_created', {
      flow,
      issuerOrigin: new URL(issuerUrl).origin,
      redirectOrigin: new URL(redirectUri).origin,
      scopeHasOpenId: scope.split(/\s+/).includes('openid'),
      transactionSaved: true,
    });
    return authorizeUrl.toString();
  } catch (error: unknown) {
    if (error instanceof CentralSsoError) {
      logCentralSsoDiagnostic('authorize_url_creation_failed', {
        flow,
        errorCode: error.code,
      });
      throw error;
    }
    logCentralSsoDiagnostic('authorize_url_creation_failed', {
      flow,
      errorCode: getCentralSsoDiagnosticErrorCode(error),
    });
    throw new CentralSsoError('central_sso_storage_failed');
  }
}

export async function startCentralSso(): Promise<void> {
  const authorizeUrl = await createAuthorizeUrl('top_level');
  logCentralSsoDiagnostic('top_level_redirect_requested', {
    flow: 'top_level',
    destinationOrigin: new URL(authorizeUrl).origin,
  });
  window.location.assign(authorizeUrl);
}

export async function startCentralSsoSilent(attemptId?: number): Promise<CentralSsoSilentAttempt> {
  const authorizeUrl = await createAuthorizeUrl('silent');
  const prepareUrl = new URL(CALLBACK_PATH, window.location.origin);
  prepareUrl.searchParams.set(SILENT_PREPARE_PARAMETER, SILENT_PREPARE_VALUE);
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.tabIndex = -1;
    iframe.style.display = 'none';
    iframe.addEventListener('load', () => {
      logCentralSsoDiagnostic('silent_iframe_load', {
        flow: 'silent',
        attemptId,
        frameOrigin: new URL(iframe.src).origin,
      });
    });
    iframe.addEventListener('error', () => {
      logCentralSsoDiagnostic('silent_iframe_error', {
        flow: 'silent',
        attemptId,
        frameOrigin: new URL(iframe.src).origin,
      });
    });
    iframe.src = prepareUrl.toString();
    document.body.appendChild(iframe);
    logCentralSsoDiagnostic('silent_iframe_created', {
      flow: 'silent',
      attemptId,
      prepareOrigin: prepareUrl.origin,
      sameOriginPrepare: prepareUrl.origin === window.location.origin,
    });
    return { iframe, authorizeUrl };
  } catch (error) {
    logCentralSsoDiagnostic('silent_iframe_creation_failed', {
      flow: 'silent',
      attemptId,
      errorCode: getCentralSsoDiagnosticErrorCode(error),
    });
    throw error;
  }
}

export function navigateCentralSsoSilentFrame(attempt: CentralSsoSilentAttempt): void {
  logCentralSsoDiagnostic('silent_authorize_navigation_requested', {
    flow: 'silent',
    destinationOrigin: new URL(attempt.authorizeUrl).origin,
  });
  attempt.iframe.src = attempt.authorizeUrl;
}

export function startCentralSsoPopup(): CentralSsoPopupAttempt {
  let popup: Window | null = null;
  try {
    popup = window.open(
      'about:blank',
      '_blank',
      'popup=yes,width=480,height=720',
    );
  } catch {
    throw new CentralSsoError('central_sso_popup_blocked');
  }
  if (!popup) {
    throw new CentralSsoError('central_sso_popup_blocked');
  }

  try {
    popup.sessionStorage.setItem(POPUP_RELAY_MARKER_KEY, randomBase64Url(32));
  } catch {
    popup.close();
    throw new CentralSsoError('central_sso_storage_failed');
  }

  const ready = createAuthorizeUrl('popup')
    .then((authorizeUrl) => {
      if (popup.closed) {
        throw new CentralSsoError('central_sso_popup_closed');
      }
      popup.location.replace(authorizeUrl);
    })
    .catch((error: unknown) => {
      clearTransaction();
      popup.close();
      if (error instanceof CentralSsoError) {
        throw error;
      }
      throw new CentralSsoError('central_sso_popup_failed');
    });

  return { popup, ready };
}

async function exchangeCode(
  issuerUrl: string,
  clientId: string,
  transaction: OAuthTransaction,
  code: string,
  flow: CentralSsoDiagnosticFlow,
): Promise<TokenResponse> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  logCentralSsoDiagnostic('token_exchange_started', {
    flow,
    issuerOrigin: new URL(issuerUrl).origin,
    codePresent: Boolean(code),
  });

  try {
    const response = await fetch(`${issuerUrl}/auth/v1/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: transaction.redirectUri,
        code_verifier: transaction.codeVerifier,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      logCentralSsoDiagnostic('token_exchange_http_error', {
        flow,
        httpStatus: response.status,
      });
      throw new CentralSsoError('central_sso_token_exchange_failed');
    }

    const tokenResponse = await response.json() as TokenResponse;
    logCentralSsoDiagnostic('token_exchange_response_received', {
      flow,
      accessTokenPresent: typeof tokenResponse.access_token === 'string',
      refreshTokenPresent: typeof tokenResponse.refresh_token === 'string',
    });
    return tokenResponse;
  } catch (error) {
    logCentralSsoDiagnostic('token_exchange_failed', {
      flow,
      errorCode: getCentralSsoDiagnosticErrorCode(error),
    });
    if (error instanceof CentralSsoError) {
      throw error;
    }
    throw new CentralSsoError('central_sso_token_exchange_failed');
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function completeCentralSsoCallbackResponse(
  client: SupabaseClient,
  { code, state: callbackState, error }: CentralSsoCallbackResponse,
  flow: CentralSsoDiagnosticFlow,
): Promise<boolean> {
  logCentralSsoDiagnostic('callback_received', {
    flow,
    ...getCallbackDiagnosticFields({ code, state: callbackState, error }),
  });
  const transaction = readTransaction(flow);
  if (!callbackState || !transaction) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: !callbackState ? 'state_missing' : 'transaction_missing',
    });
    clearTransaction();
    throw new CentralSsoError('central_sso_missing_callback');
  }

  if (callbackState !== transaction.state) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: 'state_mismatch',
      stateMatch: false,
    });
    clearTransaction();
    throw new CentralSsoError('central_sso_state_mismatch');
  }

  let config: ReturnType<typeof getConfig>;
  try {
    config = getConfig();
  } catch (configError) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: getCentralSsoDiagnosticErrorCode(configError),
    });
    clearTransaction();
    throw configError;
  }

  if (transaction.redirectUri !== config.redirectUri) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: 'redirect_uri_mismatch',
      redirectUriMatch: false,
    });
    clearTransaction();
    throw new CentralSsoError('invalid_redirect_uri');
  }

  const transactionAgeMs = Date.now() - transaction.createdAt;
  if (transactionAgeMs > TRANSACTION_MAX_AGE_MS) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: 'transaction_expired',
      transactionAgeMs,
    });
    clearTransaction();
    throw new CentralSsoError('invalid_grant');
  }

  if (error) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: 'oauth_error',
      oauthErrorCode: mapOAuthError(error),
    });
    clearTransaction();
    throw new CentralSsoError(mapOAuthError(error));
  }

  if (!code) {
    logCentralSsoDiagnostic('callback_validation_failed', {
      flow,
      reason: 'code_missing',
    });
    clearTransaction();
    throw new CentralSsoError('central_sso_missing_callback');
  }

  try {
    const tokenResponse = await exchangeCode(config.issuerUrl, config.clientId, transaction, code, flow);
    if (typeof tokenResponse.access_token !== 'string' || typeof tokenResponse.refresh_token !== 'string') {
      logCentralSsoDiagnostic('callback_session_failed', {
        flow,
        reason: 'token_response_missing_session_tokens',
        accessTokenPresent: typeof tokenResponse.access_token === 'string',
        refreshTokenPresent: typeof tokenResponse.refresh_token === 'string',
      });
      throw new CentralSsoError('central_sso_invalid_token_response');
    }

    logCentralSsoDiagnostic('wallet_session_set_started', { flow });
    const { error: sessionError } = await client.auth.setSession({
      access_token: tokenResponse.access_token,
      refresh_token: tokenResponse.refresh_token,
    });
    if (sessionError) {
      logCentralSsoDiagnostic('wallet_session_set_failed', {
        flow,
        errorStatus: typeof sessionError.status === 'number' ? sessionError.status : null,
      });
      throw new CentralSsoError('central_sso_session_failed');
    }
    logCentralSsoDiagnostic('wallet_session_set_succeeded', { flow });
  } finally {
    clearTransaction();
  }

  return true;
}

export async function completeCentralSsoPopupCallback(
  client: SupabaseClient,
  message: CentralSsoPopupCallbackMessage,
): Promise<boolean> {
  return completeCentralSsoCallbackResponse(client, message.callback, 'popup');
}

export async function completeCentralSsoSilentCallback(
  client: SupabaseClient,
  message: CentralSsoSilentCallbackMessage,
): Promise<boolean> {
  return completeCentralSsoCallbackResponse(client, message.callback, 'silent');
}

export async function completeCentralSsoCallback(
  client: SupabaseClient,
): Promise<boolean> {
  if (!isCentralSsoCallbackPath()) {
    return false;
  }

  const response = getCallbackResponse(new URLSearchParams(window.location.search));
  if (!response.error && !response.code && !response.state) {
    return false;
  }

  scrubCallbackUrl();
  return completeCentralSsoCallbackResponse(client, response, 'top_level');
}

export function completeCentralSsoCallbackOnce(
  client: SupabaseClient,
): Promise<boolean> {
  if (callbackCompletionPromise) {
    return callbackCompletionPromise;
  }

  if (!hasCentralSsoCallbackResponse()) {
    return Promise.resolve(false);
  }

  callbackCompletionPromise = completeCentralSsoCallback(client);
  return callbackCompletionPromise;
}

export function clearCentralSsoTransaction(): void {
  clearTransaction();
}
